import { useCallback } from 'react';
import { useCollectionsStore, setCollections } from '../../store/collectionsStore';
import { setRequestTabs, setActiveRequestTabId, useTabsStore, setCollectionTabs } from '../../store/tabsStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useAuthStore } from '../../store/authStore';
import { setCollectionVariablesById } from '../../store/variablesStore';
import {
  setShowCollectionSelectionModal,
  setPendingRequestData,
  setPendingRequestTabId,
  useUiModalStore,
} from '../../store/uiModalStore';
import { collectionService } from '../../services/collectionService';
import { isCloudRestWorkspace } from '../../utils/workspaceStorage';
import { mergeCollectionSettings, isFolderTab } from '../../utils/collectionTabState';
import { syncLinkedLocalCopySettings } from '../../utils/linkedLocalCopySync';

/** The pending-request collection picker and saving a collection tab. */
export function useCollectionTabSave({
  toastT,
  reloadDeviceLocalCollections,
  saveRequest,
}) {
  const handleSelectCollectionForPendingRequest = (selectedCollectionId, selectedFolderId = null, editedName) => {
    const collections = useCollectionsStore.getState().collections;
    const { pendingRequestTabId, pendingRequestData } = useUiModalStore.getState();
    const selectedCollection = collections.find(
      (collection) => String(collection.id) === String(selectedCollectionId)
    );

    if (!selectedCollection || !pendingRequestTabId || !pendingRequestData) {
      setShowCollectionSelectionModal(false);
      toastT('toast.invalidCollectionSelection', 'warning');
      return;
    }

    const trimmedName = typeof editedName === 'string' ? editedName.trim() : '';
    const targetFolderId = selectedFolderId || null;
    const requestDataToSave = trimmedName
      ? { ...pendingRequestData, name: trimmedName }
      : pendingRequestData;
    const tabIdToSave = pendingRequestTabId;

    // Associate the pending tab with the chosen collection/folder (and name) first.
    setRequestTabs((prev) => prev.map((tab) =>
      tab.id === tabIdToSave
        ? {
            ...tab,
            collectionId: selectedCollection.id,
            folderId: targetFolderId,
            ...(trimmedName ? { name: trimmedName } : {}),
          }
        : tab
    ));

    setActiveRequestTabId(tabIdToSave);
    setShowCollectionSelectionModal(false);
    setPendingRequestData(null);
    setPendingRequestTabId(null);

    // Re-run save after state update to submit with the real collection/folder ID.
    setTimeout(() => {
      saveRequest(requestDataToSave, {
        pickedCollectionId: selectedCollection.id,
        pickedFolderId: targetFolderId,
      });
    }, 0);
  };

  // ---- Collection tab (documentation / auth / variables) -------------------
  const handleCollectionTabDataChange = useCallback((patch) => {
    const activeCollectionTabId = useTabsStore.getState().activeCollectionTabId;
    if (!activeCollectionTabId) return;
    setCollectionTabs((prev) =>
      prev.map((tab) =>
        tab.id === activeCollectionTabId
          ? {
              ...tab,
              section: patch.section ?? tab.section,
              documentation: patch.documentation ?? tab.documentation,
              auth: patch.auth ?? tab.auth,
              variables: patch.variables ?? tab.variables,
              preRequestScript: patch.preRequestScript ?? tab.preRequestScript,
              testScript: patch.testScript ?? tab.testScript,
              hasUnsavedChanges:
                typeof patch.hasUnsavedChanges === 'boolean'
                  ? patch.hasUnsavedChanges
                  : tab.hasUnsavedChanges,
            }
          : tab
      )
    );
  }, []);

  const saveCollectionFromTab = useCallback(
    async (payload) => {
      const { collectionTabs, activeCollectionTabId } = useTabsStore.getState();
      const collections = useCollectionsStore.getState().collections;
      const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
      const isAuthenticated = useAuthStore.getState().isAuthenticated;
      const { deviceLocalCollections, workspaces } = useWorkspaceStore.getState();

      /**
       * WHICH tab this save is for.
       *
       * It used to be "whichever collection tab is active right now", which is
       * true for the Save button and false for anything deferred: the variables
       * editor autosaves on a debounce and flushes when it unmounts, and by
       * then the user has usually switched tabs — so the draft would have been
       * written into whatever collection they switched TO. Callers that know
       * their own tab say so; the button still does not have to.
       */
      const tabId = payload.tabId ?? activeCollectionTabId;
      const tab = collectionTabs.find((t) => t.id === tabId);
      // A folder page saves through useFolderPageSave, never into the collection.
      if (!tab?.collectionId || isFolderTab(tab)) return;

      const collectionId = tab.collectionId;
      // A deferred save carries the collection it was composed against. If the
      // tab has since been pointed at another one, the save is stale and
      // writing it would be worse than dropping it.
      if (payload.collectionId && String(payload.collectionId) !== String(collectionId)) return;
      const collection = collections.find((c) => String(c.id) === String(collectionId));
      const existingSettings = collection?.settings || {};
      const nextSettings = mergeCollectionSettings(existingSettings, {
        documentation: payload.documentation ?? '',
        collection_auth: payload.auth || { type: 'noauth' },
        collection_variables: payload.variables || [],
        // Collection scripts (owner D4) — where utils/scriptChain reads them.
        ...(typeof payload.preRequestScript === 'string' ? { preRequestScript: payload.preRequestScript } : {}),
        ...(typeof payload.testScript === 'string' ? { testScript: payload.testScript } : {}),
      });

      try {
        if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
          await collectionService.updateCollection(collectionId, { settings: nextSettings });
          await collectionService.updateVariableScopes(collectionId, {
            collection_variables: payload.variables || [],
          });
          await syncLinkedLocalCopySettings({
            collectionId,
            collection,
            nextSettings,
            deviceLocalCollections,
            workspaces,
            reloadDeviceLocalCollections,
          });
        }

        setCollections((prev) =>
          prev.map((c) =>
            String(c.id) === String(collectionId)
              ? {
                  ...c,
                  settings: nextSettings,
                  variable_scopes: {
                    ...(c.variable_scopes || {}),
                    collection_variables: payload.variables || [],
                  },
                }
              : c
          )
        );
        setCollectionVariablesById((prev) => ({
          ...prev,
          [collectionId]: payload.variables || [],
        }));
        setCollectionTabs((prev) =>
          prev.map((t) =>
            t.id === tabId
              ? {
                  ...t,
                  section: payload.section ?? t.section,
                  documentation: payload.documentation ?? t.documentation,
                  auth: payload.auth ?? t.auth,
                  variables: payload.variables ?? t.variables,
                  preRequestScript: payload.preRequestScript ?? t.preRequestScript,
                  testScript: payload.testScript ?? t.testScript,
                  hasUnsavedChanges: false,
                }
              : t
          )
        );
        // An autosave that announces itself every few seconds is noise; a
        // failure still speaks up, in the catch below.
        if (!payload.silent) toastT('toast.collectionSaved', 'success');
      } catch (error) {
        console.error('Failed to save collection:', error);
        toastT('toast.saveCollectionFailed', 'error', { message: error.message });
      }
    },
    [reloadDeviceLocalCollections, toastT]
  );

  return {
    handleSelectCollectionForPendingRequest,
    handleCollectionTabDataChange,
    saveCollectionFromTab,
  };
}
